import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import { RoundButton } from '@/components/UI/RoundButton';
import AccountSelect from '@/components/UI/AccountSelect';
import { prettyFloatMoney } from '@/helpers/utils';
import { MoneyField } from '@/components/UI/MoneyField';
import { CONTACT_EMAIL, ROLES } from '@/helpers/constants';
import WarningBlock from '@/components/UI/WarningBlock';
import { RoundLink } from '@/components/UI/RoundLink';
import { APP_LINKS } from '@/helpers/links';
import { useGetFinances } from '@/query/queries/cash/useGetFinances';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { MyLink } from '@/components/MyLink';
import { VALIDATION_SCHEMA } from './schema';
import './index.scss';

const WithdrawForm = ({ accountType, balance, accounts, onSubmit, children }) => {
  const { user, isMainFounder } = useGetMe();
  const { data: finances } = useGetFinances();
  const { t } = useTranslation('withdraw');

  return (
    <Formik
      enableReinitialize
      validationSchema={VALIDATION_SCHEMA(t)}
      onSubmit={(values, formikBag) => onSubmit(values, formikBag)}
      initialValues={{
        amount: '',
        account: (accounts && accounts[0]) || null,
      }}
    >
      {({ values, errors, touched, handleSubmit, setFieldValue }) => (
        <>
          {user.role !== ROLES.founder && (
            <WarningBlock className="withdraw-form__cofounder" withBorder>
              <div className="f-16 f-500">{t('withdraw_form.withdraw_funds_warning')}</div>
            </WarningBlock>
          )}
          <form onSubmit={handleSubmit} className="withdraw-form">
            <p className="withdraw-form__from f-16">{t('withdraw_form.from_cash_balance')}</p>
            <p className="withdraw-form__from-amount f-20 f-500">{prettyFloatMoney(balance)}</p>
            {accountType === 'business' && !!finances?.business?.frozen_amount && (
              <div className="withdraw-form__frozen">
                <p className="withdraw-form__frozen_title f-16">
                  {t('withdraw_form.reserved_amount')}
                </p>
                <p className="withdraw-form__frozen_amount f-20 f-500">
                  {prettyFloatMoney(finances.business.frozen_amount)}
                </p>
              </div>
            )}
            <p className="withdraw-form__to f-16">{t('withdraw_form.to_bank_account')}</p>
            <AccountSelect
              value={values.account}
              options={accounts || []}
              onSelect={option => setFieldValue('account', option)}
              className="withdraw-form__select"
              error={errors.account && touched.account && errors.account}
              disabled={user.role !== ROLES.founder}
              accountType={accountType}
            />
            {isMainFounder && (
              <div className="withdraw-form__add_bank_account">
                <RoundLink
                  path={APP_LINKS.bankAccountsCreate}
                  label={t('withdraw_form.add_new_bank_account')}
                />
              </div>
            )}
            <MoneyField
              label={t('withdraw_form.amount')}
              name="amount"
              onChange={(_, amount) => setFieldValue('amount', amount)}
              value={values.amount}
              onClear={() => setFieldValue('amount', '')}
              className="withdraw-form__amount"
              error={errors.amount && touched.amount && errors.amount}
              disabled={user.role !== ROLES.founder}
            />
            <p className="f-16 withdraw-form__commission">{t('withdraw_form.no_commission')}</p>
            <RoundButton
              type="submit"
              label={t('withdraw_form.withdraw')}
              onSubmit={handleSubmit}
              fillBackground
              className="withdraw-form__submit"
              disabled={user.role !== ROLES.founder}
            />
            {children}
            <div className="withdraw-form__support f-16">
              <p>{t('withdraw_form.scramble_transfers_eur')}</p>
              <p>{t('withdraw_form.currency_conversion_notice')}</p>{' '}
              {t('withdraw_form.withdrawal_processing_notice')}{' '}
              <MyLink href={`mailto:${CONTACT_EMAIL}`} isExternal underline>
                {CONTACT_EMAIL}
              </MyLink>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default WithdrawForm;
